import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import PageHeader from './PageHeader';
import * as XLSX from 'xlsx';

const CallCenterAdmin = ({ isDarkMode, userRole }) => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('lastUserId') || '';
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loadingStats, setLoadingStats] = useState(false);
  const [editingButton, setEditingButton] = useState(null);
  const [detailModal, setDetailModal] = useState(null); // { buttonDescription, userId, userName, companies, loading }
  const currentMonthKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const [ratingMonth, setRatingMonth] = useState(currentMonthKey);
  const [ratingCallcenterUser, setRatingCallcenterUser] = useState('');
  const [ratingRaterUser, setRatingRaterUser] = useState('');
  const [ratingSearch, setRatingSearch] = useState('');
  const [ratingRows, setRatingRows] = useState([]);
  const [ratingMonths, setRatingMonths] = useState([]);
  const [ratingCallcenterUsers, setRatingCallcenterUsers] = useState([]);
  const [ratingRaters, setRatingRaters] = useState([]);
  const [ratingOverallAvg, setRatingOverallAvg] = useState(0);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [revertingLeadId, setRevertingLeadId] = useState('');
  const [deletingStatId, setDeletingStatId] = useState('');
  const [deletingRatingCompanyId, setDeletingRatingCompanyId] = useState('');
  const [moveBatchModal, setMoveBatchModal] = useState(null); // { row }
  const [moveBatchTargetId, setMoveBatchTargetId] = useState('');
  const [movingLeadId, setMovingLeadId] = useState('');
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [addLeadForm, setAddLeadForm] = useState({ searchQuery: '', foundCompanies: [], selectedCompany: null, selectedButtonId: '', callcenterUserId: '', assignedToUserId: '' });
  const [addLeadSearchLoading, setAddLeadSearchLoading] = useState(false);
  const [addLeadSubmitting, setAddLeadSubmitting] = useState(false);
  const [allUsers, setAllUsers] = useState([]);

  // Tab state
  const [activeTab, setActiveTab] = useState('config');

  // Provolat lists state
  const [provolatLists, setProvolatLists] = useState([]);
  const [provolatLoading, setProvolatLoading] = useState(false);
  const [newProvolatLabel, setNewProvolatLabel] = useState('');
  const [provolatCreating, setProvolatCreating] = useState(false);
  const [provolatOpenListId, setProvolatOpenListId] = useState(null);
  const [provolatIcosText, setProvolatIcosText] = useState({});
  const [provolatSavingIcos, setProvolatSavingIcos] = useState(false);

  const openDetail = async (buttonDescription, userId, userName) => {
    setDetailModal({ buttonDescription, userId, userName, companies: [], loading: true });
    try {
      const params = `?buttonDescription=${encodeURIComponent(buttonDescription)}&month=${selectedMonth}&year=${selectedYear}${userId ? `&userId=${userId}` : ''}`;
      const res = await fetch(`${API_URL}/callcenter/stats/batch-detail${params}`);
      const data = res.ok ? await res.json() : [];
      setDetailModal({ buttonDescription, userId, userName, companies: data, loading: false });
    } catch {
      setDetailModal({ buttonDescription, userId, userName, companies: [], loading: false });
    }
  };

  const handleRevertLead = async (row) => {
    if (!row?.statId) return;
    const ok = window.confirm(`Opravdu chcete zrušit započtení leadu pro ${row.companyName || 'firmu'} a vrátit lead uživateli ${row.userName || 'callcentrum'}?`);
    if (!ok) return;

    try {
      setRevertingLeadId(String(row.statId));
      const response = await fetch(`${API_URL}/callcenter/stats/${row.statId}/revert?userId=${userId}`, {
        method: 'DELETE'
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Nepodařilo se zrušit započtení leadu');
      }

      setDetailModal(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          companies: (prev.companies || []).filter(c => String(c.statId) !== String(row.statId))
        };
      });

      await fetchStats();
      await fetchLeadRatings();
    } catch (error) {
      console.error('Chyba při vracení leadu:', error);
      alert(error.message || 'Chyba při vracení leadu');
    } finally {
      setRevertingLeadId('');
    }
  };

  const handleDeleteStat = async (row) => {
    if (!row?.statId) return;
    const ok = window.confirm(`Opravdu chcete smazat tento záznam ze statistiky pro ${row.companyName || 'firmu'}? Lead zůstane přiřazen, smaže se pouze záznam ze statistiky.`);
    if (!ok) return;

    try {
      setDeletingStatId(String(row.statId));
      const response = await fetch(`${API_URL}/callcenter/stats/${row.statId}/delete?userId=${userId}`, {
        method: 'DELETE'
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Nepodařilo se smazat záznam ze statistiky');
      }

      setDetailModal(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          companies: (prev.companies || []).filter(c => String(c.statId) !== String(row.statId))
        };
      });

      await fetchStats();
    } catch (error) {
      console.error('Chyba při mazání záznamu ze statistiky:', error);
      alert(error.message || 'Chyba při mazání záznamu ze statistiky');
    } finally {
      setDeletingStatId('');
    }
  };

  const handleDeleteLeadRating = async (row) => {
    if (!row?.companyId) return;
    const ok = window.confirm(`Opravdu chcete smazat hodnocení leadu pro ${row.companyName || 'tuto firmu'}?`);
    if (!ok) return;

    try {
      setDeletingRatingCompanyId(String(row.companyId));
      const response = await fetch(`${API_URL}/companies/${row.companyId}/lead-rating?userId=${userId}`, {
        method: 'DELETE'
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Nepodařilo se smazat hodnocení leadu');
      }

      setRatingRows(prev => prev.filter(r => String(r.companyId) !== String(row.companyId)));
      await fetchLeadRatings();
    } catch (error) {
      console.error('Chyba při mazání hodnocení leadu:', error);
      alert(error.message || 'Chyba při mazání hodnocení leadu');
    } finally {
      setDeletingRatingCompanyId('');
    }
  };

  const handleMoveLead = async () => {
    const row = moveBatchModal?.row;
    if (!row?.statId || !moveBatchTargetId) return;

    const targetButton = config?.buttons?.find(b => b._id === moveBatchTargetId);
    if (!targetButton) return;

    const ok = window.confirm(`Opravdu chcete přesunout lead pro ${row.companyName || 'firmu'} do šarže "${targetButton.batchName}"?`);
    if (!ok) return;

    try {
      setMovingLeadId(String(row.statId));
      const response = await fetch(`${API_URL}/callcenter/stats/${row.statId}/move-batch?userId=${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newButtonId: moveBatchTargetId })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Nepodařilo se přesunout lead');
      }

      // Odeber lead z aktuálního detailu a zavři modal přesunu
      setDetailModal(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          companies: (prev.companies || []).filter(c => String(c.statId) !== String(row.statId))
        };
      });
      setMoveBatchModal(null);
      setMoveBatchTargetId('');
      await fetchStats();
    } catch (error) {
      console.error('Chyba při přesunu leadu:', error);
      alert(error.message || 'Chyba při přesunu leadu');
    } finally {
      setMovingLeadId('');
    }
  };

  useEffect(() => {
    fetchConfig();
    fetchStats();
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    // Načti uživatele pro formulář manuálního přidání leadu
    const loadUsers = async () => {
      try {
        const res = await fetch(`${API_URL}/users?userId=${userId}`);
        if (res.ok) {
          const data = await res.json();
          setAllUsers(Array.isArray(data) ? data : (data.users || []));
        }
      } catch (e) {
        console.error('Chyba při načítání uživatelů:', e);
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    fetchLeadRatings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratingMonth, ratingCallcenterUser, ratingRaterUser, ratingSearch]);

  const searchCompanyForLead = async () => {
    const q = addLeadForm.searchQuery.trim();
    if (!q) return;
    setAddLeadSearchLoading(true);
    try {
      const res = await fetch(`${API_URL}/companies?search=${encodeURIComponent(q)}&userId=${userId}&globalSearch=true&limit=10`);
      const data = res.ok ? await res.json() : {};
      setAddLeadForm(prev => ({ ...prev, foundCompanies: Array.isArray(data) ? data : (data.companies || []), selectedCompany: null }));
    } catch {
      setAddLeadForm(prev => ({ ...prev, foundCompanies: [] }));
    } finally {
      setAddLeadSearchLoading(false);
    }
  };

  const handleAddManualLead = async () => {
    const { selectedCompany, selectedButtonId, callcenterUserId, assignedToUserId } = addLeadForm;
    if (!selectedCompany || !selectedButtonId || !callcenterUserId || !assignedToUserId) {
      alert('Vyplňte všechna povinná pole');
      return;
    }
    setAddLeadSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/callcenter/stats/manual?userId=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: selectedCompany._id,
          buttonId: selectedButtonId,
          callcenterUserId,
          assignedToUserId,
          month: selectedMonth,
          year: selectedYear
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Chyba při přidávání leadu');
      alert('Lead byl úspěšně přidán do statistiky a provizí');
      setShowAddLeadModal(false);
      setAddLeadForm({ searchQuery: '', foundCompanies: [], selectedCompany: null, selectedButtonId: '', callcenterUserId: '', assignedToUserId: '' });
      await fetchStats();
    } catch (error) {
      alert(error.message || 'Chyba při přidávání leadu');
    } finally {
      setAddLeadSubmitting(false);
    }
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      console.log('📞 Načítám call center konfiguraci z:', `${API_URL}/callcenter/config`);
      const response = await fetch(`${API_URL}/callcenter/config?userId=${userId}`);
      console.log('📞 Response status:', response.status);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('📞 Načtená konfigurace:', data);
      setConfig(data);
    } catch (error) {
      console.error('❌ Chyba při načítání konfigurace:', error);
      // Nastavit výchozí prázdnou konfiguraci
      setConfig({ buttons: [] });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      const response = await fetch(
        `${API_URL}/callcenter/stats/summary?month=${selectedMonth}&year=${selectedYear}&userId=${userId}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Chyba při načítání statistik:', error);
      setStats([]);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchLeadRatings = async () => {
    try {
      setRatingLoading(true);
      const params = new URLSearchParams({
        month: ratingMonth,
        userId
      });
      if (ratingCallcenterUser) params.set('callCenterUserId', ratingCallcenterUser);
      if (ratingRaterUser) params.set('ratedById', ratingRaterUser);
      if (ratingSearch.trim()) params.set('search', ratingSearch.trim());

      const response = await fetch(`${API_URL}/callcenter/lead-ratings?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setRatingRows(data.rows || []);
      setRatingMonths(data.monthsAvailable || []);
      setRatingCallcenterUsers(data.callcenterUsers || []);
      setRatingRaters(data.raters || []);
      setRatingOverallAvg(Number(data.overallAvg || 0));

      if ((data.monthsAvailable || []).length > 0 && !data.monthsAvailable.includes(ratingMonth)) {
        setRatingMonth(data.monthsAvailable[0]);
      }
    } catch (error) {
      console.error('Chyba při načítání hodnocení leadů:', error);
      setRatingRows([]);
      setRatingOverallAvg(0);
    } finally {
      setRatingLoading(false);
    }
  };



  const handleAddButton = () => {
    const newButton = {
      description: 'Nové tlačítko',
      assignTo: 'kontakty@vyjedname.cz',
      batchName: 'Nová šarže',
      value: 0,
      color: 'purple',
      order: (config?.buttons?.length || 0) + 1,
      isActive: true,
      _id: `temp-${Date.now()}`
    };
    
    setConfig({
      ...config,
      buttons: [...(config?.buttons || []), newButton]
    });
    
    setEditingButton(newButton._id);
  };

  const handleUpdateButton = (buttonId, field, value) => {
    setConfig({
      ...config,
      buttons: config.buttons.map(btn =>
        btn._id === buttonId ? { ...btn, [field]: value } : btn
      )
    });
  };

  const handleDeleteButton = (buttonId) => {
    if (!window.confirm('Opravdu chcete smazat toto tlačítko?')) return;
    
    setConfig({
      ...config,
      buttons: config.buttons.filter(btn => btn._id !== buttonId)
    });
  };

  const handleMoveButton = (index, direction) => {
    const newButtons = [...config.buttons];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newButtons.length) return;
    
    [newButtons[index], newButtons[targetIndex]] = [newButtons[targetIndex], newButtons[index]];
    
    // Update order
    newButtons.forEach((btn, idx) => {
      btn.order = idx + 1;
    });
    
    setConfig({ ...config, buttons: newButtons });
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      
      // Očistit dočasná ID
      const buttonsToSave = config.buttons.map(btn => {
        const { _id, ...rest } = btn;
        if (_id && _id.toString().startsWith('temp-')) {
          return rest;
        }
        return btn;
      });
      
      const response = await fetch(`${API_URL}/callcenter/config?userId=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buttons: buttonsToSave })
      });
      
      if (!response.ok) throw new Error('Chyba při ukládání');
      
      const savedConfig = await response.json();
      setConfig(savedConfig);
      setEditingButton(null);
      alert('Konfigurace byla úspěšně uložena');
    } catch (error) {
      console.error('Chyba při ukládání konfigurace:', error);
      alert('Chyba při ukládání konfigurace');
    } finally {
      setSaving(false);
    }
  };

  // =================== PROVOLAT LISTS FUNCTIONS ===================
  const fetchProvolatLists = async () => {
    try {
      setProvolatLoading(true);
      const res = await fetch(`${API_URL}/callcenter/provolat-lists?userId=${userId}`);
      const data = res.ok ? await res.json() : [];
      setProvolatLists(Array.isArray(data) ? data : []);
    } catch {
      setProvolatLists([]);
    } finally {
      setProvolatLoading(false);
    }
  };

  const handleCreateProvolatList = async () => {
    if (!newProvolatLabel.trim()) return;
    setProvolatCreating(true);
    try {
      const res = await fetch(`${API_URL}/callcenter/provolat-lists?userId=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: newProvolatLabel.trim() })
      });
      if (res.ok) {
        setNewProvolatLabel('');
        await fetchProvolatLists();
      } else {
        const d = await res.json().catch(() => ({}));
        alert(d.error || 'Chyba při vytváření seznamu');
      }
    } catch {
      alert('Chyba při připojení k serveru');
    } finally {
      setProvolatCreating(false);
    }
  };

  const handleSaveProvolatIcos = async (listId) => {
    const text = provolatIcosText[listId] || '';
    const icos = text.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
    setProvolatSavingIcos(true);
    try {
      const res = await fetch(`${API_URL}/callcenter/provolat-lists/${listId}?userId=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ icos })
      });
      if (res.ok) {
        await fetchProvolatLists();
        alert(`Uloženo ${icos.length} IČO`);
      } else {
        const d = await res.json().catch(() => ({}));
        alert(d.error || 'Chyba při ukládání IČO');
      }
    } catch {
      alert('Chyba při připojení k serveru');
    } finally {
      setProvolatSavingIcos(false);
    }
  };

  const handleDeleteProvolatList = async (listId, label) => {
    if (!window.confirm(`Opravdu smazat seznam "${label}"? Tato akce je nevratná.`)) return;
    try {
      await fetch(`${API_URL}/callcenter/provolat-lists/${listId}?userId=${userId}`, { method: 'DELETE' });
      await fetchProvolatLists();
      if (provolatOpenListId === listId) setProvolatOpenListId(null);
    } catch {
      alert('Chyba při mazání seznamu');
    }
  };

  const handleRemoveExtension = async (listId, ico) => {
    try {
      await fetch(`${API_URL}/callcenter/provolat-lists/${listId}/extensions/${encodeURIComponent(ico)}?userId=${userId}`, { method: 'DELETE' });
      await fetchProvolatLists();
    } catch {
      alert('Chyba při mazání záznamu');
    }
  };

  const handleExportExtensions = (list) => {
    const extensions = list.extensions || [];
    if (extensions.length === 0) {
      alert('Žádné záznamy k exportu');
      return;
    }
    const wsData = [['IČO', 'Prodloužit'], ...extensions.map(e => [e.ico, list.label])];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Prodloužení');
    XLSX.writeFile(wb, `provolat-prodlouzeni-${list._id}.xlsx`);
  };
  // =================== END PROVOLAT LISTS FUNCTIONS ===================

  const getMonthName = (month) => {
    const months = [
      'Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen',
      'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'
    ];
    return months[month - 1];
  };

  const colorOptions = [
    { value: 'purple', label: 'Fialová', class: 'bg-purple-500' },
    { value: 'blue', label: 'Modrá', class: 'bg-blue-500' },
    { value: 'green', label: 'Zelená', class: 'bg-green-500' },
    { value: 'red', label: 'Červená', class: 'bg-red-500' },
    { value: 'orange', label: 'Oranžová', class: 'bg-orange-500' },
    { value: 'pink', label: 'Růžová', class: 'bg-pink-500' },
    { value: 'indigo', label: 'Indigo', class: 'bg-indigo-500' }
  ];

  // Seskupit statistiky podle uživatelů
  const groupedStats = stats.reduce((acc, stat) => {
    if (!acc[stat.userName]) {
      acc[stat.userName] = {
        userName: stat.userName,
        userId: stat.userId,
        buttons: {}
      };
    }
    
    if (!acc[stat.userName].buttons[stat.buttonDescription]) {
      acc[stat.userName].buttons[stat.buttonDescription] = {
        count: 0,
        totalValue: 0,
        companies: []
      };
    }
    
    acc[stat.userName].buttons[stat.buttonDescription].count += stat.count;
    acc[stat.userName].buttons[stat.buttonDescription].totalValue += (stat.totalValue || 0);
    acc[stat.userName].buttons[stat.buttonDescription].companies.push(...stat.companies);
    
    return acc;
  }, {});

  const selectedCallcenterAvg = (() => {
    if (ratingRows.length === 0) return 0;
    const baseRows = ratingCallcenterUser
      ? ratingRows.filter(r => String(r.callCenterUserId) === String(ratingCallcenterUser))
      : ratingRows;
    if (baseRows.length === 0) return 0;
    const total = baseRows.reduce((sum, r) => sum + Number(r.score || 0), 0);
    return Math.round((total / baseRows.length) * 100) / 100;
  })();

  if (loading) {
    return (
      <div className={`p-8 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white'}`}>
        <div className="text-center">Načítání...</div>
      </div>
    );
  }

  // BEZPEČNOSTNÍ KONTROLA - pouze pro adminy (musí být AŽ PO hooks a před hlavním return)
  if (userRole !== 'admin' && userRole !== 'teamleader' && userRole !== 'superadmin') {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-8">
            <h1 className="text-2xl font-bold text-red-600 mb-4">🚫 Přístup odepřen</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4">Tato stránka je dostupná pouze administrátorům.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
            >
              Zpět na hlavní stránku
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (<>
    <div className={`p-8 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white'} min-h-screen`}>
      <div className="max-w-7xl mx-auto">
        {/* Tlačítko zpět */}
        <PageHeader
          title="📞 Správa Call Centra"
          backPath="/users"
        />

        {/* Tab lišta */}
        <div className={`flex flex-wrap border-b mb-6 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {[
            { id: 'config', label: '⚙️ Konfigurace' },
            { id: 'stats', label: '📊 Statistika leadů' },
            { id: 'ratings', label: '⭐ Hodnocení callcentra' },
            { id: 'provolat', label: '📋 Provolat seznamy' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); if (tab.id === 'provolat' && provolatLists.length === 0) fetchProvolatLists(); }}
              className={`px-5 py-3 font-medium text-sm border-b-2 transition whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-500'
                  : `border-transparent ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Konfigurace tlačítek */}
        {activeTab === 'config' && (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-6 mb-8`}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold">⚙️ Konfigurace tlačítek</h2>
            <div className="flex gap-2">
              <button
                onClick={handleAddButton}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Přidat tlačítko
              </button>
              <button
                onClick={handleSaveConfig}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold disabled:bg-gray-400"
              >
                {saving ? 'Ukládání...' : 'Uložit změny'}
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {config?.buttons?.map((button, index) => (
              <div
                key={button._id}
                className={`${isDarkMode ? 'bg-gray-700' : 'bg-white'} rounded-lg p-4 border-2 ${
                  editingButton === button._id ? 'border-blue-500' : 'border-transparent'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Order controls */}
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => handleMoveButton(index, 'up')}
                      disabled={index === 0}
                      className="p-1 hover:bg-gray-600 rounded disabled:opacity-30"
                    >
                      ▲
                    </button>
                    <span className="text-center font-bold">{index + 1}</span>
                    <button
                      onClick={() => handleMoveButton(index, 'down')}
                      disabled={index === config.buttons.length - 1}
                      className="p-1 hover:bg-gray-600 rounded disabled:opacity-30"
                    >
                      ▼
                    </button>
                  </div>

                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Popis */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Popis tlačítka:</label>
                      <input
                        type="text"
                        value={button.description}
                        onChange={(e) => handleUpdateButton(button._id, 'description', e.target.value)}
                        onFocus={() => setEditingButton(button._id)}
                        className={`w-full px-3 py-2 rounded border ${
                          isDarkMode
                            ? 'bg-gray-600 border-gray-500 text-white'
                            : 'bg-white border-gray-300'
                        }`}
                        placeholder="např. Lead 100 Kč - podklady nadiktované"
                      />
                    </div>

                    {/* Šarže */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Název šarže:</label>
                      <input
                        type="text"
                        value={button.batchName}
                        onChange={(e) => handleUpdateButton(button._id, 'batchName', e.target.value)}
                        onFocus={() => setEditingButton(button._id)}
                        className={`w-full px-3 py-2 rounded border ${
                          isDarkMode
                            ? 'bg-gray-600 border-gray-500 text-white'
                            : 'bg-white border-gray-300'
                        }`}
                        placeholder="např. Lead 100 Kč - nadiktované podklady"
                      />
                    </div>

                    {/* Přidělit komu */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Přidělit komu (email):</label>
                      <input
                        type="email"
                        value={button.assignTo}
                        onChange={(e) => handleUpdateButton(button._id, 'assignTo', e.target.value)}
                        onFocus={() => setEditingButton(button._id)}
                        className={`w-full px-3 py-2 rounded border ${
                          isDarkMode
                            ? 'bg-gray-600 border-gray-500 text-white'
                            : 'bg-white border-gray-300'
                        }`}
                        placeholder="kontakty@vyjedname.cz"
                      />
                    </div>

                    {/* Hodnota pro provize */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Hodnota (Kč) pro provize:</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={button.value || 0}
                        onChange={(e) => handleUpdateButton(button._id, 'value', parseFloat(e.target.value) || 0)}
                        onFocus={() => setEditingButton(button._id)}
                        className={`w-full px-3 py-2 rounded border ${
                          isDarkMode
                            ? 'bg-gray-600 border-gray-500 text-white'
                            : 'bg-white border-gray-300'
                        }`}
                        placeholder="100"
                      />
                    </div>

                    {/* Barva */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Barva:</label>
                      <select
                        value={button.color}
                        onChange={(e) => handleUpdateButton(button._id, 'color', e.target.value)}
                        onFocus={() => setEditingButton(button._id)}
                        className={`w-full px-3 py-2 rounded border ${
                          isDarkMode
                            ? 'bg-gray-600 border-gray-500 text-white'
                            : 'bg-white border-gray-300'
                        }`}
                      >
                        {colorOptions.map(color => (
                          <option key={color.value} value={color.value}>
                            {color.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={button.isActive}
                        onChange={(e) => handleUpdateButton(button._id, 'isActive', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Aktivní</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!button.leadRatingEnabled}
                        onChange={(e) => handleUpdateButton(button._id, 'leadRatingEnabled', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Hodnocení leadů</span>
                    </label>
                    <button
                      onClick={() => handleDeleteButton(button._id)}
                      className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm"
                    >
                      🗑️ Smazat
                    </button>
                  </div>
                </div>

                {/* Preview */}
                <div className="mt-4 pt-4 border-t border-gray-600">
                  <p className="text-sm text-gray-400 mb-2">Náhled tlačítka:</p>
                  <div className="flex gap-2">
                    <button
                      className={`bg-${button.color}-500 hover:bg-${button.color}-600 text-white py-2 px-4 rounded-lg transition font-semibold text-xs flex items-center justify-center gap-1 shadow-lg`}
                      style={{
                        backgroundColor: colorOptions.find(c => c.value === button.color)?.class.replace('bg-', '').includes(button.color) ? undefined : '#8b5cf6'
                      }}
                    >
                      Odeslat
                    </button>
                    <button
                      className={`bg-${button.color}-600 hover:bg-${button.color}-700 text-white py-2 px-4 rounded-lg transition font-semibold text-xs flex items-center justify-center gap-1 shadow-lg`}
                      style={{
                        backgroundColor: colorOptions.find(c => c.value === button.color)?.class.replace('bg-', '').includes(button.color) ? undefined : '#7c3aed'
                      }}
                    >
                      Odeslat a zpět na seznam
                    </button>
                    <button
                      className={`bg-${button.color}-700 hover:bg-${button.color}-800 text-white py-2 px-4 rounded-lg transition font-semibold text-xs flex items-center justify-center gap-1 shadow-lg`}
                      style={{
                        backgroundColor: colorOptions.find(c => c.value === button.color)?.class.replace('bg-', '').includes(button.color) ? undefined : '#6d28d9'
                      }}
                    >
                      Odeslat a další firma
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {(!config?.buttons || config.buttons.length === 0) && (
              <div className="text-center py-8 text-gray-500">
                Zatím nejsou vytvořena žádná tlačítka. Klikněte na "Přidat tlačítko" pro vytvoření prvního.
              </div>
            )}
          </div>
        </div>
        )}

        {/* Statistiky Call Centra */}
        {activeTab === 'stats' && (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-6`}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold">📊 Statistiky leadů</h2>
            <div className="flex gap-4 items-center">
              <button
                onClick={() => setShowAddLeadModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold"
              >
                ➕ Přidat lead manuálně
              </button>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className={`px-4 py-2 rounded border ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <option key={month} value={month}>
                    {getMonthName(month)}
                  </option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className={`px-4 py-2 rounded border ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <button
                onClick={fetchStats}
                disabled={loadingStats}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
              >
                {loadingStats ? 'Načítání...' : 'Obnovit'}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <th className="px-4 py-3 text-left font-semibold">Uživatel</th>
                  {config?.buttons?.filter(b => b.isActive).map(button => (
                    <th key={button._id} className="px-4 py-3 text-center font-semibold">
                      {button.description}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center font-semibold">Celkem</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(groupedStats).map((userStat, index) => {
                  const total = Object.values(userStat.buttons).reduce((sum, btn) => sum + btn.count, 0);
                  
                  const totalValue = Object.values(userStat.buttons).reduce(
                    (sum, btn) => sum + (btn.totalValue || 0), 
                    0
                  );
                  
                  return (
                    <tr
                      key={userStat.userId}
                      className={`border-b ${
                        isDarkMode ? 'border-gray-700' : 'border-gray-200'
                      } ${index % 2 === 0 ? (isDarkMode ? 'bg-gray-800' : 'bg-white') : (isDarkMode ? 'bg-gray-750' : 'bg-gray-50')}`}
                    >
                      <td className="px-4 py-3 font-medium">{userStat.userName}</td>
                      {config?.buttons?.filter(b => b.isActive).map(button => {
                        const count = userStat.buttons[button.description]?.count || 0;
                        const totalValue = userStat.buttons[button.description]?.totalValue || 0;
                        return (
                          <td key={button._id} className="px-4 py-3 text-center">
                            {count > 0 ? (
                              <div className="flex flex-col items-center gap-1">
                                <button
                                  onClick={() => openDetail(button.description, userStat.userId, userStat.userName)}
                                  className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-full font-semibold transition cursor-pointer"
                                  title="Zobrazit seznam firem"
                                >
                                  {count}
                                </button>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {totalValue.toLocaleString('cs-CZ')} Kč
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-400">0</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className="inline-block bg-green-600 text-white px-4 py-1 rounded-full font-bold">
                            {total}
                          </span>
                          <span className="text-xs font-semibold text-green-600 dark:text-green-400">
                            {totalValue.toLocaleString('cs-CZ')} Kč
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                
                {Object.keys(groupedStats).length === 0 && (
                  <tr>
                    <td
                      colSpan={config?.buttons?.filter(b => b.isActive).length + 2}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      Žádné statistiky pro vybraný měsíc
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Hodnocení leadů callcentra */}
        {activeTab === 'ratings' && (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-6 mt-8`}>
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <h2 className="text-2xl font-semibold">⭐ Hodnocení callcentra</h2>
              <button
                onClick={fetchLeadRatings}
                disabled={ratingLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg self-start md:self-auto"
              >
                {ratingLoading ? 'Načítání...' : 'Obnovit'}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Měsíc hodnocení</label>
                <select
                  value={ratingMonth}
                  onChange={(e) => setRatingMonth(e.target.value)}
                  className={`w-full px-3 py-2 rounded border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                >
                  {(ratingMonths.length > 0 ? ratingMonths : [ratingMonth]).map(monthKey => (
                    <option key={monthKey} value={monthKey}>{monthKey}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Callcentrum uživatel</label>
                <select
                  value={ratingCallcenterUser}
                  onChange={(e) => setRatingCallcenterUser(e.target.value)}
                  className={`w-full px-3 py-2 rounded border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="">Všichni</option>
                  {ratingCallcenterUsers.map(u => (
                    <option key={u._id} value={u._id}>{u.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Kdo hodnotil</label>
                <select
                  value={ratingRaterUser}
                  onChange={(e) => setRatingRaterUser(e.target.value)}
                  className={`w-full px-3 py-2 rounded border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="">Všichni</option>
                  {ratingRaters.map(u => (
                    <option key={u._id} value={u._id}>{u.name}</option>
                  ))}
                </select>
              </div>

              <div className="lg:col-span-2">
                <label className="block text-sm font-medium mb-1">Hledat IČO / zákazníka</label>
                <input
                  type="text"
                  value={ratingSearch}
                  onChange={(e) => setRatingSearch(e.target.value)}
                  placeholder="Např. 12345678 nebo Název firmy"
                  className={`w-full px-3 py-2 rounded border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className={`rounded-lg px-4 py-3 ${isDarkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
                <div className="text-sm text-gray-500 dark:text-gray-400">Průměr hodnocení {ratingCallcenterUser ? 'vybraného callcentra' : 'za měsíc'}</div>
                <div className="text-2xl font-bold text-yellow-500">{selectedCallcenterAvg.toFixed(2)} / 5</div>
              </div>
              <div className={`rounded-lg px-4 py-3 ${isDarkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
                <div className="text-sm text-gray-500 dark:text-gray-400">Celkový průměr (aktuální filtr)</div>
                <div className="text-2xl font-bold text-blue-500">{ratingOverallAvg.toFixed(2)} / 5</div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <th className="px-4 py-3 text-left font-semibold">Měsíc</th>
                  <th className="px-4 py-3 text-left font-semibold">Kdo lead vytvořil</th>
                  <th className="px-4 py-3 text-left font-semibold">Kdo hodnotil</th>
                  <th className="px-4 py-3 text-left font-semibold">IČO zákazníka</th>
                  <th className="px-4 py-3 text-left font-semibold">Jméno zákazníka</th>
                  <th className="px-4 py-3 text-center font-semibold">Hodnocení</th>
                  <th className="px-4 py-3 text-left font-semibold">Datum hodnocení</th>
                  <th className="px-4 py-3 text-right font-semibold">Akce</th>
                </tr>
              </thead>
              <tbody>
                {ratingRows.map((row, index) => (
                  <tr
                    key={`${row.companyIco}-${row.ratedAt}-${index}`}
                    className={`border-b ${
                      isDarkMode ? 'border-gray-700' : 'border-gray-200'
                    } ${index % 2 === 0 ? (isDarkMode ? 'bg-gray-800' : 'bg-white') : (isDarkMode ? 'bg-gray-750' : 'bg-gray-50')}`}
                  >
                    <td className="px-4 py-3">{row.month}</td>
                    <td className="px-4 py-3 font-medium">{row.callCenterUserName || '—'}</td>
                    <td className="px-4 py-3">{row.ratedByName || '—'}</td>
                    <td className="px-4 py-3">{row.companyIco || '—'}</td>
                    <td className="px-4 py-3">{row.companyName || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 font-semibold text-yellow-500">
                        {`${Number(row.score || 0).toFixed(1)} ★`}
                      </span>
                    </td>
                    <td className="px-4 py-3">{row.ratedAt ? new Date(row.ratedAt).toLocaleString('cs-CZ') : '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDeleteLeadRating(row)}
                        disabled={deletingRatingCompanyId === String(row.companyId)}
                        className="text-xs px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white disabled:bg-gray-500"
                      >
                        {deletingRatingCompanyId === String(row.companyId) ? 'Mažu...' : 'Smazat'}
                      </button>
                    </td>
                  </tr>
                ))}

                {ratingRows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      Žádné hodnocení leadů pro vybrané filtry
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Provolat seznamy */}
        {activeTab === 'provolat' && (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-6`}>
          <h2 className="text-2xl font-semibold mb-6">📋 Provolat seznamy</h2>

          {/* Nový seznam */}
          <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
            <h3 className="font-semibold mb-3">Vytvořit nový seznam</h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={newProvolatLabel}
                onChange={e => setNewProvolatLabel(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreateProvolatList()}
                placeholder='Např. "Provolat do 20.7.2026"'
                className={`flex-1 px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'bg-white border-gray-300'}`}
              />
              <button
                onClick={handleCreateProvolatList}
                disabled={provolatCreating || !newProvolatLabel.trim()}
                className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:bg-gray-400"
              >
                {provolatCreating ? 'Vytvářím...' : 'Vytvořit'}
              </button>
            </div>
          </div>

          {/* Loading */}
          {provolatLoading && <div className="text-center py-8 text-gray-500">Načítám...</div>}

          {/* Lists */}
          {!provolatLoading && provolatLists.length === 0 && (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Zatím nemáte žádné Provolat seznamy. Vytvořte první výše.
            </div>
          )}

          <div className="space-y-4">
            {provolatLists.map(list => {
              const isOpen = provolatOpenListId === list._id;
              return (
                <div key={list._id} className={`rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                  {/* List header */}
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setProvolatOpenListId(isOpen ? null : list._id)}
                        className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}
                      >
                        {isOpen ? '▼' : '▶'} {list.label}
                      </button>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                        {(list.icos || []).length} IČO
                      </span>
                      {(list.extensions || []).length > 0 && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                          {list.extensions.length} prodloužení
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteProvolatList(list._id, list.label)}
                      className="text-xs px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white"
                    >
                      Smazat
                    </button>
                  </div>

                  {/* Expanded content */}
                  {isOpen && (
                    <div className={`px-4 pb-4 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-100'}`}>
                      {/* IČO import */}
                      <div className="mt-4">
                        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          IČO (jedno na řádek, nebo oddělena čárkou / středníkem) — aktuálně {(list.icos || []).length} IČO
                        </label>
                        <textarea
                          rows={6}
                          value={provolatIcosText[list._id] !== undefined ? provolatIcosText[list._id] : (list.icos || []).join('\n')}
                          onChange={e => setProvolatIcosText(prev => ({ ...prev, [list._id]: e.target.value }))}
                          placeholder="12345678&#10;87654321&#10;..."
                          className={`w-full px-3 py-2 rounded border text-sm font-mono ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'bg-white border-gray-300'}`}
                        />
                        <button
                          onClick={() => handleSaveProvolatIcos(list._id)}
                          disabled={provolatSavingIcos}
                          className="mt-2 px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:bg-gray-400"
                        >
                          {provolatSavingIcos ? 'Ukládám...' : 'Uložit IČO'}
                        </button>
                      </div>

                      {/* Extension requests */}
                      {(list.extensions || []).length > 0 && (
                        <div className="mt-6">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={`font-semibold text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                              📅 Žádosti o prodloužení o +1 měsíc ({list.extensions.length})
                            </h4>
                            <button
                              onClick={() => handleExportExtensions(list)}
                              className="text-xs px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                            >
                              ⬇ Export do Excelu
                            </button>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className={`${isDarkMode ? 'bg-gray-600' : 'bg-gray-100'}`}>
                                  <th className="px-3 py-2 text-left font-semibold">IČO</th>
                                  <th className="px-3 py-2 text-left font-semibold">Datum žádosti</th>
                                  <th className="px-3 py-2 text-right font-semibold">Akce</th>
                                </tr>
                              </thead>
                              <tbody>
                                {list.extensions.map((ext, i) => (
                                  <tr key={i} className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-100'}`}>
                                    <td className="px-3 py-2 font-mono">{ext.ico}</td>
                                    <td className="px-3 py-2">{ext.requestedAt ? new Date(ext.requestedAt).toLocaleString('cs-CZ') : '—'}</td>
                                    <td className="px-3 py-2 text-right">
                                      <button
                                        onClick={() => handleRemoveExtension(list._id, ext.ico)}
                                        className="text-xs px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white"
                                      >
                                        Smazat
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {(list.extensions || []).length === 0 && (
                        <p className={`mt-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Žádné žádosti o prodloužení.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        )}

      </div>
    </div>

    {/* Detail modal */}
    {detailModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4" onClick={() => setDetailModal(null)}>
        <div
          className={`rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
          onClick={e => e.stopPropagation()}
        >
          <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div>
              <h3 className="font-bold text-base">📋 {detailModal.buttonDescription}</h3>
              {detailModal.userName && <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{detailModal.userName}</p>}
            </div>
            <button onClick={() => setDetailModal(null)} className={`text-xl ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>✕</button>
          </div>
          <div className="overflow-auto flex-1 p-4">
            {detailModal.loading ? (
              <div className="text-center py-10 text-gray-500">Načítám...</div>
            ) : detailModal.companies.length === 0 ? (
              <div className={`text-center py-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Žádné firmy.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-200'}`}>
                    <th className="px-3 py-2 text-left font-semibold">Název firmy</th>
                    <th className="px-3 py-2 text-left font-semibold">IČO</th>
                    <th className="px-3 py-2 text-left font-semibold">Přiděleno</th>
                    <th className="px-3 py-2 text-left font-semibold">Čas přidělení</th>
                    <th className="px-3 py-2 text-left font-semibold">Stav</th>
                    <th className="px-3 py-2 text-left font-semibold">FC</th>
                    <th className="px-3 py-2 text-right font-semibold">Akce</th>
                  </tr>
                </thead>
                <tbody>
                  {detailModal.companies.map((c, i) => (
                    <tr key={i} className={`border-b ${isDarkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-gray-50'}`}>
                      <td className="px-3 py-2 font-medium">{c.companyName || '—'}</td>
                      <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{c.ico || '—'}</td>
                      <td className={`px-3 py-2 font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>{c.ownerName || '—'}</td>
                      <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{c.createdAt ? new Date(c.createdAt).toLocaleString('cs-CZ') : '—'}</td>
                      <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{c.status || '—'}</td>
                      <td className="px-3 py-2">
                        {c.forecast ? (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.forecast === '100' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {c.forecast}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex gap-1 justify-end">
                          <button
                            onClick={() => { setMoveBatchModal({ row: c }); setMoveBatchTargetId(''); }}
                            disabled={movingLeadId === String(c.statId)}
                            className="text-xs px-2 py-1 rounded bg-yellow-500 hover:bg-yellow-600 text-white disabled:bg-gray-500"
                            title="Přesunout lead do jiné šarže"
                          >
                            Přesunout
                          </button>
                          <button
                            onClick={() => handleDeleteStat(c)}
                            disabled={deletingStatId === String(c.statId)}
                            className="text-xs px-2 py-1 rounded bg-orange-600 hover:bg-orange-700 text-white disabled:bg-gray-500"
                            title="Smazat ze statistiky (lead zůstane přiřazen)"
                          >
                            {deletingStatId === String(c.statId) ? 'Mažu...' : 'Smazat ze statistiky'}
                          </button>
                          <button
                            onClick={() => handleRevertLead(c)}
                            disabled={revertingLeadId === String(c.statId)}
                            className="text-xs px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white disabled:bg-gray-500"
                            title="Smazat započtení leadu a vrátit na callcentrum"
                          >
                            {revertingLeadId === String(c.statId) ? 'Zpracování...' : 'Vrátit lead'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    )}
    {/* Move batch modal */}
    {moveBatchModal && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-70 p-4" onClick={() => setMoveBatchModal(null)}>
        <div
          className={`rounded-xl shadow-2xl w-full max-w-md ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
          onClick={e => e.stopPropagation()}
        >
          <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className="font-bold text-base">🔀 Přesunout lead do jiné šarže</h3>
            <button onClick={() => setMoveBatchModal(null)} className={`text-xl ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>✕</button>
          </div>
          <div className="p-5 space-y-4">
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Firma: <span className="font-semibold">{moveBatchModal.row.companyName || '—'}</span>
            </p>
            <div>
              <label className="block text-sm font-medium mb-1">Cílová šarže:</label>
              <select
                value={moveBatchTargetId}
                onChange={e => setMoveBatchTargetId(e.target.value)}
                className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              >
                <option value="">— Vyberte šarži —</option>
                {(config?.buttons || []).filter(b => b.isActive && b._id !== moveBatchModal.row.buttonId).map(b => (
                  <option key={b._id} value={b._id}>{b.description} ({b.batchName})</option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <button
                onClick={() => setMoveBatchModal(null)}
                className={`px-4 py-2 rounded text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                Zrušit
              </button>
              <button
                onClick={handleMoveLead}
                disabled={!moveBatchTargetId || movingLeadId === String(moveBatchModal.row.statId)}
                className="px-4 py-2 rounded text-sm bg-yellow-500 hover:bg-yellow-600 text-white font-semibold disabled:bg-gray-400"
              >
                {movingLeadId === String(moveBatchModal.row.statId) ? 'Přesouvám...' : 'Přesunout'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    {/* Manual add lead modal */}
    {showAddLeadModal && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-70 p-4" onClick={() => setShowAddLeadModal(false)}>
        <div
          className={`rounded-xl shadow-2xl w-full max-w-lg ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
          onClick={e => e.stopPropagation()}
        >
          <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className="font-bold text-base">➕ Přidat lead manuálně</h3>
            <button onClick={() => setShowAddLeadModal(false)} className={`text-xl ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>✕</button>
          </div>
          <div className="p-5 space-y-4">
            {/* Company search */}
            <div>
              <label className="block text-sm font-medium mb-1">Hledat firmu (IČO nebo název):</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={addLeadForm.searchQuery}
                  onChange={e => setAddLeadForm(prev => ({ ...prev, searchQuery: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && searchCompanyForLead()}
                  placeholder="Zadejte IČO nebo název firmy..."
                  className={`flex-1 px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300'}`}
                />
                <button
                  onClick={searchCompanyForLead}
                  disabled={addLeadSearchLoading}
                  className="px-3 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:bg-gray-400"
                >
                  {addLeadSearchLoading ? '...' : 'Hledat'}
                </button>
              </div>
              {addLeadForm.foundCompanies.length > 0 && !addLeadForm.selectedCompany && (
                <div className={`mt-2 rounded border max-h-40 overflow-y-auto ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-white'}`}>
                  {addLeadForm.foundCompanies.map(c => (
                    <div
                      key={c._id}
                      className={`px-3 py-2 cursor-pointer text-sm hover:${isDarkMode ? 'bg-gray-600' : 'bg-gray-50'} border-b last:border-0 ${isDarkMode ? 'border-gray-600' : 'border-gray-100'}`}
                      onClick={() => setAddLeadForm(prev => ({ ...prev, selectedCompany: c, foundCompanies: [] }))}
                    >
                      <span className="font-medium">{c.name}</span>
                      {c.ico && <span className={`ml-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>IČO: {c.ico}</span>}
                    </div>
                  ))}
                </div>
              )}
              {addLeadForm.selectedCompany && (
                <div className={`mt-2 px-3 py-2 rounded text-sm flex justify-between items-center ${isDarkMode ? 'bg-green-900 text-green-200' : 'bg-green-50 text-green-800'}`}>
                  <span>✓ <strong>{addLeadForm.selectedCompany.name}</strong>{addLeadForm.selectedCompany.ico ? ` (IČO: ${addLeadForm.selectedCompany.ico})` : ''}</span>
                  <button onClick={() => setAddLeadForm(prev => ({ ...prev, selectedCompany: null, searchQuery: '' }))} className="text-xs underline">Změnit</button>
                </div>
              )}
            </div>

            {/* Batch selection */}
            <div>
              <label className="block text-sm font-medium mb-1">Šarže / typ leadu:</label>
              <select
                value={addLeadForm.selectedButtonId}
                onChange={e => setAddLeadForm(prev => ({ ...prev, selectedButtonId: e.target.value }))}
                className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              >
                <option value="">— Vyberte šarži —</option>
                {(config?.buttons || []).filter(b => b.isActive).map(b => (
                  <option key={b._id} value={b._id}>{b.description} ({b.batchName}) — {b.value} Kč</option>
                ))}
              </select>
            </div>

            {/* Callcenter user */}
            <div>
              <label className="block text-sm font-medium mb-1">Kdo lead udělal (callcenter):
                <span className={`ml-1 text-xs font-normal ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>→ určuje řádek ve statistice</span>
              </label>
              <select
                value={addLeadForm.callcenterUserId}
                onChange={e => setAddLeadForm(prev => ({ ...prev, callcenterUserId: e.target.value }))}
                className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              >
                <option value="">— Vyberte uživatele —</option>
                {allUsers.filter(u => u.role === 'callcentrum').map(u => (
                  <option key={u._id} value={u._id}>{u.name || u.email}</option>
                ))}
                {allUsers.filter(u => u.role === 'callcentrum').length === 0 && allUsers.map(u => (
                  <option key={u._id} value={u._id}>{u.name || u.email}</option>
                ))}
              </select>
            </div>

            {/* Assigned-to user */}
            <div>
              <label className="block text-sm font-medium mb-1">Komu šel lead (obchodník):</label>
              <select
                value={addLeadForm.assignedToUserId}
                onChange={e => setAddLeadForm(prev => ({ ...prev, assignedToUserId: e.target.value }))}
                className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              >
                <option value="">— Vyberte uživatele —</option>
                {allUsers.filter(u => u.role !== 'callcentrum').map(u => (
                  <option key={u._id} value={u._id}>{u.name || u.email}</option>
                ))}
              </select>
            </div>

            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Lead bude přidán do měsíce <strong>{getMonthName(selectedMonth)} {selectedYear}</strong>
            </p>

            <div className="flex gap-3 justify-end pt-2">
              <button
                onClick={() => { setShowAddLeadModal(false); setAddLeadForm({ searchQuery: '', foundCompanies: [], selectedCompany: null, selectedButtonId: '', callcenterUserId: '', assignedToUserId: '' }); }}
                className={`px-4 py-2 rounded text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                Zrušit
              </button>
              <button
                onClick={handleAddManualLead}
                disabled={addLeadSubmitting || !addLeadForm.selectedCompany || !addLeadForm.selectedButtonId || !addLeadForm.callcenterUserId || !addLeadForm.assignedToUserId}
                className="px-4 py-2 rounded text-sm bg-green-600 hover:bg-green-700 text-white font-semibold disabled:bg-gray-400"
              >
                {addLeadSubmitting ? 'Přidávám...' : 'Přidat lead'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
  </>
  );
};

export default CallCenterAdmin;
